/**
 * Platform-aware RevenueCat entitlement read.
 *
 * WHY THIS EXISTS — the bug it fixes (found 2026-07-28):
 *
 * `useProAccess` (the app's only access gate) checked Pro status via
 * `hasProAccess()` / `getSubscriptionDetails()` from `lib/revenuecat.ts`.
 * Those read a module-level `purchasesInstance` that is populated ONLY by that
 * module's own `initializeRevenueCat()` — which is imported in
 * `lib/AuthContext.tsx` but NEVER CALLED (only `logoutUser` is). Nothing else
 * calls it either: `useRevenueCat.initialize` has no consumers, and
 * `RevenueCatPaywall` configures the purchases-js shared instance directly,
 * which does not populate that module's instance.
 *
 * Net effect: `purchasesInstance` was permanently null, so `hasProAccess()`
 * returned `false` for EVERY user on EVERY platform, forever. A subscriber who
 * actually paid was never recognised as Pro. The OPMODE 14-day Firestore trial
 * masked this for new accounts — the moment that trial lapsed, a paying
 * customer hit the hard paywall.
 *
 * DESIGN — why not just call the web initializeRevenueCat():
 *
 * The web SDK (`@revenuecat/purchases-js`) pulls in Stripe, and loading Stripe
 * eagerly broke Next.js's error overlay — that is precisely why the web init
 * was deferred until a paywall opened (see lib/revenuecatIdentity.ts). So we
 * do NOT force it on. Instead:
 *
 *   - native  → the Capacitor SDK, which is already configured at sign-in and
 *               is the authoritative source for App Store / Play entitlements.
 *   - web     → RevenueCat's REST API with the PUBLIC web key. One GET, no
 *               Stripe, no SDK. Read-only entitlement check.
 *
 * Both collapse to the same shape so `useProAccess` doesn't care which ran.
 */

import { isNative } from './platformDetection';
import { ENTITLEMENT_ID, planTypeFromProductId } from './revenuecat';

export interface EntitlementStatus {
  /** True when the OPMODE Pro entitlement is currently active. */
  isActive: boolean;
  /** True when that entitlement is active *because of* a store free trial. */
  isInTrial: boolean;
  /** ISO-8601 expiry of the current period, or null. */
  expirationDate: string | null;
  /** Which plan is entitling them, for banner copy. */
  subscriptionType: 'monthly' | 'yearly' | 'none';
  /** Where the answer came from — useful when debugging a wrong verdict. */
  source: 'native' | 'web-rest' | 'unavailable';
}

const NONE: EntitlementStatus = {
  isActive: false,
  isInTrial: false,
  expirationDate: null,
  subscriptionType: 'none',
  source: 'unavailable',
};

function toIso(value: unknown): string | null {
  if (!value) return null;
  const d = new Date(value as string);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

/**
 * Native path — Capacitor SDK.
 *
 * Re-asserts identity first. `registerUserWithRevenueCat` swallows a failed
 * `Purchases.logIn`, which would leave the SDK on the PREVIOUS user and make
 * us read the wrong customer's entitlements. Calling initializeRevenueCat here
 * is a no-op when the identity is already correct, and a repair when it isn't.
 */
async function readNative(userId: string): Promise<EntitlementStatus> {
  const { initializeRevenueCat, getCustomerInfo } = await import('./iap/revenueCat');

  try {
    await initializeRevenueCat(userId);
  } catch (e) {
    // Identity switch failed. Reading now would report another account's
    // entitlements, so refuse to answer rather than answer wrongly.
    console.error('[entitlement] identity assert failed; not reading:', e);
    return { ...NONE, source: 'unavailable' };
  }

  const customerInfo = await getCustomerInfo();
  const ent = customerInfo?.entitlements?.active?.[ENTITLEMENT_ID];
  if (!ent) {
    return { ...NONE, source: 'native' };
  }

  // Native SDK reports periodType in UPPERCASE ('TRIAL' | 'INTRO' | 'NORMAL').
  const periodType = String(ent.periodType ?? '').toUpperCase();

  return {
    isActive: true,
    isInTrial: periodType === 'TRIAL',
    expirationDate: toIso(ent.expirationDate),
    subscriptionType: planTypeFromProductId(ent.productIdentifier),
    source: 'native',
  };
}

/**
 * Web path — REST, public key only. Never loads the SDK (and so never Stripe).
 */
async function readWebRest(userId: string): Promise<EntitlementStatus> {
  const apiKey =
    process.env.NEXT_PUBLIC_REVENUECAT_WEB_KEY ||
    process.env.NEXT_PUBLIC_REVENUECAT_API_KEY ||
    '';

  // Never let a secret key reach the client, and don't bother without a key.
  if (!apiKey || apiKey.startsWith('sk_')) return { ...NONE, source: 'unavailable' };

  const resp = await fetch(
    `https://api.revenuecat.com/v1/subscribers/${encodeURIComponent(userId)}`,
    { headers: { Authorization: `Bearer ${apiKey}`, 'X-Platform': 'web' } },
  );
  if (!resp.ok) return { ...NONE, source: 'unavailable' };

  const body = await resp.json();
  const subscriber = body?.subscriber;
  const ent = subscriber?.entitlements?.[ENTITLEMENT_ID];
  if (!ent) return { ...NONE, source: 'web-rest' };

  // REST reports entitlements without an isActive flag — compare expiry.
  // A null expires_date means a lifetime/non-expiring grant.
  const expires = ent.expires_date ? new Date(ent.expires_date).getTime() : null;
  const isActive = expires === null || expires > Date.now();
  if (!isActive) return { ...NONE, source: 'web-rest' };

  const productId: string | undefined = ent.product_identifier;
  // REST reports period_type in lowercase ('trial' | 'intro' | 'normal').
  const periodType = String(
    (productId && subscriber?.subscriptions?.[productId]?.period_type) ?? '',
  ).toLowerCase();

  return {
    isActive: true,
    isInTrial: periodType === 'trial',
    expirationDate: toIso(ent.expires_date),
    subscriptionType: planTypeFromProductId(productId),
    source: 'web-rest',
  };
}

/**
 * Read the caller's OPMODE Pro entitlement.
 *
 * Fails CLOSED on error (returns not-entitled) — but note the caller must not
 * treat that as "revoke access": `source: 'unavailable'` means we could not
 * determine the answer, which is different from a confirmed no.
 */
export async function fetchEntitlementStatus(
  userId: string | null | undefined,
): Promise<EntitlementStatus> {
  if (!userId) return NONE;
  try {
    return isNative() ? await readNative(userId) : await readWebRest(userId);
  } catch (error) {
    console.error('[entitlement] lookup failed:', error);
    return { ...NONE, source: 'unavailable' };
  }
}
